import { createContext, useContext, useEffect, useState } from 'react'

const ThemeCtx = createContext(null)
const KEY = 'clixtrack_theme'
const PRESET_KEY = 'clixtrack_preset_theme'

const ACCENT_FAMILIES = [
  { name:'Emerald', dark:'#10b981', light:'#047857', bright:'#34d399', darkContrast:'#041b13' },
  { name:'Ocean', dark:'#38bdf8', light:'#0369a1', bright:'#7dd3fc', darkContrast:'#062033' },
  { name:'Royal Purple', dark:'#a78bfa', light:'#6d28d9', bright:'#c4b5fd', darkContrast:'#170b2a' },
  { name:'Gold', dark:'#fbbf24', light:'#b45309', bright:'#fcd34d', darkContrast:'#2a1d02' },
  { name:'Rose', dark:'#fb7185', light:'#be123c', bright:'#fda4af', darkContrast:'#31060d' },
  { name:'Cyan', dark:'#22d3ee', light:'#0e7490', bright:'#67e8f9', darkContrast:'#03282e' },
  { name:'Lime', dark:'#a3e635', light:'#4d7c0f', bright:'#bef264', darkContrast:'#172404' },
  { name:'Cherry', dark:'#f43f5e', light:'#be123c', bright:'#fb7185', darkContrast:'#330710' },
  { name:'Sapphire', dark:'#60a5fa', light:'#1d4ed8', bright:'#93c5fd', darkContrast:'#071b3d' },
  { name:'Orange', dark:'#fb923c', light:'#c2410c', bright:'#fdba74', darkContrast:'#351304' },
  { name:'Teal', dark:'#2dd4bf', light:'#0f766e', bright:'#5eead4', darkContrast:'#042823' },
  { name:'Indigo', dark:'#818cf8', light:'#4338ca', bright:'#a5b4fc', darkContrast:'#10143a' },
  { name:'Pink', dark:'#f472b6', light:'#be185d', bright:'#f9a8d4', darkContrast:'#360920' },
  { name:'Amber', dark:'#f59e0b', light:'#b45309', bright:'#fbbf24', darkContrast:'#321a02' },
  { name:'Violet', dark:'#c084fc', light:'#7e22ce', bright:'#d8b4fe', darkContrast:'#250638' },
  { name:'Sky', dark:'#0ea5e9', light:'#0369a1', bright:'#38bdf8', darkContrast:'#041d2b' },
  { name:'Mint', dark:'#6ee7b7', light:'#047857', bright:'#a7f3d0', darkContrast:'#06291d' },
  { name:'Coral', dark:'#fb7185', light:'#c2415b', bright:'#fda4af', darkContrast:'#360911' },
  { name:'Blue', dark:'#3b82f6', light:'#1d4ed8', bright:'#60a5fa', darkContrast:'#071a3a' },
  { name:'Magenta', dark:'#e879f9', light:'#a21caf', bright:'#f0abfc', darkContrast:'#300634' },
  { name:'Jade', dark:'#34d399', light:'#059669', bright:'#6ee7b7', darkContrast:'#05251a' },
  { name:'Ruby', dark:'#ef4444', light:'#b91c1c', bright:'#f87171', darkContrast:'#ffffff' },
  { name:'Electric', dark:'#67e8f9', light:'#0891b2', bright:'#a5f3fc', darkContrast:'#06303a' },
  { name:'Plum', dark:'#d946ef', light:'#86198f', bright:'#e879f9', darkContrast:'#ffffff' },
  { name:'Slate', dark:'#94a3b8', light:'#334155', bright:'#cbd5e1', darkContrast:'#111827' },
]

const DARK_FOUNDATIONS = [
  { name:'Pure Black', bg:'#050505', surface:'#111111', soft:'#1a1a1a', border:'#303030', text:'#f7f7f7', muted:'#a3a3a3' },
  { name:'Midnight', bg:'#070b12', surface:'#0e1623', soft:'#151f2f', border:'#29384d', text:'#f1f5fb', muted:'#94a3b8' },
  { name:'Graphite', bg:'#0d0f12', surface:'#171a1f', soft:'#20252c', border:'#373e49', text:'#f5f7fa', muted:'#9ca6b4' },
  { name:'Obsidian', bg:'#08090c', surface:'#12141a', soft:'#1b1e26', border:'#323744', text:'#f5f7ff', muted:'#9ca6ba' },
  { name:'Ink', bg:'#090b10', surface:'#121722', soft:'#1b2230', border:'#303b4f', text:'#f2f5fa', muted:'#96a3b8' },
  { name:'Charcoal', bg:'#111315', surface:'#1a1e21', soft:'#242a2e', border:'#3c454b', text:'#f4f7f8', muted:'#a1adb2' },
  { name:'Night Forest', bg:'#07100c', surface:'#0e1a14', soft:'#16261d', border:'#2a4335', text:'#f0f8f3', muted:'#94aa9d' },
  { name:'Deep Navy', bg:'#040a16', surface:'#091326', soft:'#101d34', border:'#233b61', text:'#f0f5ff', muted:'#91a4c4' },
  { name:'Black Plum', bg:'#100812', surface:'#1b1020', soft:'#26172c', border:'#462b50', text:'#faf2fc', muted:'#af9ab5' },
  { name:'Dark Cocoa', bg:'#110c09', surface:'#1e1510', soft:'#2a1e17', border:'#4b382d', text:'#fbf5f1', muted:'#b2a095' },
]

const LIGHT_FOUNDATIONS = [
  { name:'Pure White', bg:'#ffffff', surface:'#ffffff', soft:'#f4f6f8', border:'#dce1e7', text:'#111827', muted:'#667085' },
  { name:'Pearl', bg:'#f8fafc', surface:'#ffffff', soft:'#eef2f6', border:'#d5dde6', text:'#172033', muted:'#68758a' },
  { name:'Ivory', bg:'#fffdf7', surface:'#ffffff', soft:'#f6f1e5', border:'#e2d8c5', text:'#29231a', muted:'#786d5d' },
  { name:'Cloud', bg:'#f3f6fa', surface:'#ffffff', soft:'#e8edf4', border:'#ced7e3', text:'#182234', muted:'#65748a' },
  { name:'Snow', bg:'#fbfdff', surface:'#ffffff', soft:'#edf3f7', border:'#d3e0e8', text:'#12202b', muted:'#607381' },
  { name:'Mint White', bg:'#f3faf7', surface:'#ffffff', soft:'#e5f2ec', border:'#c8ddd3', text:'#14271f', muted:'#61776d' },
  { name:'Rose White', bg:'#fff7f9', surface:'#ffffff', soft:'#f8e9ee', border:'#e7cfd7', text:'#301c23', muted:'#7e6570' },
  { name:'Blue White', bg:'#f4f8ff', surface:'#ffffff', soft:'#e7eef9', border:'#cbd8eb', text:'#15243a', muted:'#637692' },
  { name:'Lavender White', bg:'#faf7ff', surface:'#ffffff', soft:'#eee8f8', border:'#d9cdeb', text:'#291f38', muted:'#746689' },
  { name:'Warm White', bg:'#fdf9f3', surface:'#ffffff', soft:'#f3ebe0', border:'#dfd1bf', text:'#2d251b', muted:'#786c5d' },
]

const ACCENT_STYLES = [
  { name:'Classic', luminous:false },
  { name:'Luminous', luminous:true },
]

function readableContrast(hex) {
  const value = hex.replace('#', '')
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 155 ? '#101318' : '#ffffff'
}

function makePreset(accent, foundation, mode, variant, style) {
  const baseAccent = mode === 'dark' ? accent.dark : accent.light
  const primary = style.luminous ? accent.bright : baseAccent
  const secondary = style.luminous ? baseAccent : accent.bright
  return {
    name: `${mode === 'dark' ? 'Black' : 'White'} ${foundation.name} · ${accent.name} ${style.name} ${String(variant + 1).padStart(2, '0')}`,
    mode,
    bg:foundation.bg,
    surface:foundation.surface,
    soft:foundation.soft,
    border:foundation.border,
    text:foundation.text,
    muted:foundation.muted,
    accent:primary,
    accent2:secondary,
    contrast:readableContrast(primary),
  }
}

function buildPresetCollection(style) {
  return ACCENT_FAMILIES.flatMap((accent) =>
    DARK_FOUNDATIONS.flatMap((darkFoundation, variant) => [
      makePreset(accent, darkFoundation, 'dark', variant, style),
      makePreset(accent, LIGHT_FOUNDATIONS[variant], 'light', variant, style),
    ]),
  )
}

// 25 accent families × 10 foundations × 2 modes × 2 finishes = 1000 themes.
// The first 500 preserve the Classic set; the next 500 add Luminous variations.
export const PRESET_THEMES = ACCENT_STYLES.flatMap((style) =>
  buildPresetCollection(style),
)

const VARS = ['--canvas','--surface','--surface-muted','--surface-raised','--border','--border-strong','--text','--text-soft','--muted','--muted-soft','--accent','--accent-strong','--accent-bright','--accent-contrast','--accent-soft','--accent-grad','--accent-glow','--input-bg','--overlay','--sidebar','--sidebar-border','--sidebar-text','--sidebar-title','--sidebar-heading','--sidebar-hover','--shadow-sm','--shadow-md']

function clearPreset(root) {
  VARS.forEach((name) => root.style.removeProperty(name))
}

function applyPreset(root, p) {
  const alpha = p.accent + '24'
  const values = {
    '--canvas':p.bg, '--surface':p.surface, '--surface-muted':p.soft, '--surface-raised':p.surface,
    '--border':p.border, '--border-strong':p.border, '--text':p.text, '--text-soft':p.text,
    '--muted':p.muted, '--muted-soft':p.soft, '--accent':p.accent, '--accent-strong':p.accent,
    '--accent-bright':p.accent2, '--accent-contrast':p.contrast, '--accent-soft':alpha,
    '--accent-grad':`linear-gradient(135deg, ${p.accent} 0%, ${p.accent2} 100%)`,
    '--accent-glow':`0 8px 24px ${p.accent}55`, '--input-bg':p.soft,
    '--overlay':p.mode === 'dark' ? 'rgba(0,0,0,.68)' : 'rgba(15,23,42,.42)',
    '--sidebar':`linear-gradient(180deg, ${p.surface} 0%, ${p.bg} 100%)`,
    '--sidebar-border':p.border, '--sidebar-text':p.muted, '--sidebar-title':p.text,
    '--sidebar-heading':p.muted, '--sidebar-hover':alpha,
    '--shadow-sm':p.mode === 'dark' ? '0 1px 3px rgba(0,0,0,.45)' : '0 1px 3px rgba(15,23,42,.08)',
    '--shadow-md':p.mode === 'dark' ? '0 14px 34px rgba(0,0,0,.55)' : '0 12px 30px rgba(15,23,42,.14)',
  }
  Object.entries(values).forEach(([name,value]) => root.style.setProperty(name, value))
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem(KEY) || 'dark')
  const [presetIndex, setPresetIndex] = useState(() => {
    const raw = localStorage.getItem(PRESET_KEY)
    const n = raw === null ? null : Number(raw)
    return Number.isInteger(n) && n >= 0 && n < PRESET_THEMES.length ? n : null
  })

  useEffect(() => {
    const root = document.documentElement
    const preset = presetIndex === null ? null : PRESET_THEMES[presetIndex]
    const mode = preset?.mode || theme
    root.classList.toggle('light', mode === 'light')
    root.classList.toggle('dark', mode !== 'light')
    clearPreset(root)
    if (preset) applyPreset(root, preset)
    localStorage.setItem(KEY, theme)
    if (presetIndex === null) localStorage.removeItem(PRESET_KEY)
    else localStorage.setItem(PRESET_KEY, String(presetIndex))
  }, [theme, presetIndex])

  const toggle = () => {
    setPresetIndex(null)
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'))
  }
  const cyclePreset = () => {
    const next = presetIndex === null ? 0 : (presetIndex + 1) % PRESET_THEMES.length
    setPresetIndex(next)
    setTheme(PRESET_THEMES[next].mode)
  }
  const selectPreset = (index) => {
    const next = Number(index)
    if (!Number.isInteger(next) || next < 0 || next >= PRESET_THEMES.length) return
    setPresetIndex(next)
    setTheme(PRESET_THEMES[next].mode)
  }
  const resetPreset = () => setPresetIndex(null)
  const preset = presetIndex === null ? null : { ...PRESET_THEMES[presetIndex], index:presetIndex, total:PRESET_THEMES.length }

  return <ThemeCtx.Provider value={{ theme, toggle, preset, cyclePreset, selectPreset, resetPreset }}>{children}</ThemeCtx.Provider>
}

export const useTheme = () => useContext(ThemeCtx)
